// =====================
// Imports
// =====================
import { ConfirmDialog } from '@/components/dialog/confirm-dialog';
import PayoutBatchStatusBadge from '@/components/payouts/PayoutBatchStatusBadge';
import PlatformFeeInfo from '@/components/payouts/PlatformFeeInfo';
import VatInfo from '@/components/payouts/VatInfo';
import PaginationControls from '@/components/shared/PaginationControls';
import TableEmptyState from '@/components/table/TableEmptyState';
import TableSkeleton from '@/components/table/TableSkeleton';
import {
  TableScrollArea,
  stickyColumnProps,
  stickyEndColumnProps,
} from '@/components/table/TableScrollArea';
import Money from '@/components/text/money';
import PayoutBatchRowActions from '@/pages/payments/payouts/PayoutBatchRowActions';
import { useCancelPayoutAction } from '@/hooks/actions/use-cancel-payout-action';
import { useContinuePayoutAction } from '@/hooks/actions/use-continue-payout-action';
import type { Pagination } from '@/types/pagination';
import { Box, Flex, Table, Text, VStack } from '@chakra-ui/react';
import type { PayoutBatchListItem } from '@fluenceur/types';
import { MoneySquare } from 'iconoir-react';
import { parseAsString, useQueryState } from 'nuqs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

// =====================
// Interfaces
// =====================
interface PayoutBatchesTableProps {
  payouts: PayoutBatchListItem[];
  pagination?: Pagination | null;
  loading?: boolean;
  onPageChange?: (page: number) => void;
}

// =====================
// Main Component
// =====================
export const PayoutBatchesTable: React.FC<PayoutBatchesTableProps> = ({
  payouts,
  pagination,
  loading = false,
  onPageChange,
}) => {
  const { t } = useTranslation();
  const [, setPayoutId] = useQueryState('payout', parseAsString);
  const { handleContinue, isValidating } = useContinuePayoutAction();
  const { cancelPayout, isCancelling } = useCancelPayoutAction();

  // State for cancel confirmation dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [batchToCancel, setBatchToCancel] = useState<string | null>(null);

  const handleCancelClick = (batchId: string) => {
    setBatchToCancel(batchId);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = () => {
    if (batchToCancel) {
      cancelPayout(batchToCancel);
      setCancelDialogOpen(false);
      setBatchToCancel(null);
    }
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
    setBatchToCancel(null);
  };

  return (
    <Box pb={8}>
      {loading ? (
        <TableSkeleton
          rows={8}
          columns={[
            { width: '160px', type: 'date' },
            { width: '140px' },
            { width: '200px' },
            { width: '180px' },
            { width: '140px' },
            { width: '190px', type: 'date' },
            { width: '190px' },
          ]}
          stickyColumns={1}
          stickyLastColumn
          firstColumnWidth="160px"
          lastColumnWidth="190px"
        />
      ) : (
        <TableScrollArea>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader width="160px" {...stickyColumnProps('160px')}>
                  {t('payouts.table.columns.created')}
                </Table.ColumnHeader>
                <Table.ColumnHeader width="140px">
                  {t('payouts.table.columns.status')}
                </Table.ColumnHeader>
                <Table.ColumnHeader width="200px">
                  {t('payouts.table.columns.totalPaidCommissions')}
                </Table.ColumnHeader>
                <Table.ColumnHeader width="180px">
                  {t('payouts.table.columns.totalCharged')}
                </Table.ColumnHeader>
                <Table.ColumnHeader width="140px">
                  {t('payouts.table.columns.nbAffiliates')}
                </Table.ColumnHeader>
                <Table.ColumnHeader width="190px">Scheduled/Completed</Table.ColumnHeader>
                <Table.ColumnHeader width="190px" {...stickyEndColumnProps('190px')} />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {payouts.length === 0 ? (
                <TableEmptyState
                  colSpan={7}
                  icon={<MoneySquare width={22} height={22} />}
                  title={t('payouts.table.empty')}
                />
              ) : (
                payouts.map((payout) => (
                  <Table.Row key={payout.id} _hover={{ bg: 'gray.50' }}>
                    <Table.Cell width="160px" {...stickyColumnProps('160px')}>
                      <Flex direction="column">
                        <Text fontSize="sm">{formatDate(payout.createdAt)}</Text>
                        <Text fontSize="xs" color="gray.500">
                          {formatTime(payout.createdAt)}
                        </Text>
                      </Flex>
                    </Table.Cell>
                    <Table.Cell width="140px">
                      <PayoutBatchStatusBadge
                        status={payout.status}
                        fundedAt={payout.fundedAt}
                        lastPaymentFailedAt={payout.lastPaymentFailedAt}
                        closedReason={payout.closedReason}
                        size="sm"
                      />
                    </Table.Cell>
                    <Table.Cell width="200px">
                      <VStack align="stretch" rowGap={0}>
                        <Money
                          fontSize="14px"
                          value={payout.totalCommissionAmount.amount}
                          currency={payout.totalCommissionAmount.currencyCode}
                        />
                        {payout.totalCollectedPlatformFee && (
                          <PlatformFeeInfo
                            amount={payout.totalCollectedPlatformFee.amount}
                            currency={payout.totalCollectedPlatformFee.currencyCode}
                          />
                        )}
                      </VStack>
                    </Table.Cell>
                    <Table.Cell width="180px">
                      <VStack align="stretch" rowGap={0}>
                        <Money
                          fontSize="14px"
                          value={payout.totalChargeAmount.amount}
                          currency={payout.totalChargeAmount.currencyCode}
                        />
                        {payout.vatAmount && (
                          <VatInfo
                            amount={payout.vatAmount.amount}
                            currency={payout.vatAmount.currencyCode}
                          />
                        )}
                      </VStack>
                    </Table.Cell>
                    <Table.Cell width="140px">
                      <Text fontSize="sm">{payout.affiliateCount}</Text>
                    </Table.Cell>
                    <Table.Cell width="190px">
                      <Flex direction="column">
                        <Text fontSize="sm">
                          {payout.completedAt
                            ? formatDate(payout.completedAt)
                            : formatDate(payout.scheduledPayoutAt || null)}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {payout.completedAt
                            ? `${t('payouts.table.completed')} ${formatTime(payout.completedAt)}`
                            : `${t('payouts.table.scheduled')} ${formatTime(payout.scheduledPayoutAt || null)}`}
                        </Text>
                      </Flex>
                    </Table.Cell>
                    <Table.Cell width="190px" textAlign="right" {...stickyEndColumnProps('190px')}>
                      <PayoutBatchRowActions
                        payout={payout}
                        isValidating={isValidating}
                        isCancelling={isCancelling}
                        onContinue={(batch) => handleContinue(batch.id, batch.status)}
                        onCancel={handleCancelClick}
                        onSeeDetails={setPayoutId}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </TableScrollArea>
      )}

      {pagination && onPageChange && pagination.total > 1 && (
        <PaginationControls
          pagination={pagination}
          onPageChange={onPageChange}
          isLoading={loading}
        />
      )}

      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        isOpen={cancelDialogOpen}
        title={t('payouts.cancelBatch.dialog.title')}
        description={t('payouts.cancelBatch.dialog.description')}
        confirmText={t('payouts.cancelBatch.dialog.confirm')}
        cancelText={t('common.cancel')}
        confirmColorPalette="red"
        onConfirm={handleCancelConfirm}
        onCancel={handleCancelDialogClose}
        isLoading={isCancelling}
      />
    </Box>
  );
};

// =====================
// Utility Functions
// =====================
const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return 'Invalid Date';
  }
};

const formatTime = (dateString: string | null): string => {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};
